/**
 * Queue Archive — every queuer across the admin's active location(s)
 * (Queues-section tab, route `queues.archive`). Read-only history on the
 * Events-list paradigm: List/Cards toggle, search + outcome/queue/location
 * filters, click-to-sort columns, per-row detail view via the action cluster.
 * Data: listQueueArchive (declared op).
 */
import { useMemo, useState, type CSSProperties } from "react";
import { Btn, Icon, IconBtn, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, FilterBar, FilterDropdown, EmptyState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, tabExport } from "../../shell/list-composites";
import { useIsMobile } from "../../shell/useResponsive";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useWfResource } from "../workforce/useWorkforce";
import { LocationFilter, WfPending } from "../workforce/wf-shared";
import { listQueueArchive, type ArchiveEntry } from "./queues-api";
import { queuesSectionTabs, useQueueSectionCounts, SORT_ARROW } from "./queues-section";

import { useT } from "@v0y4ge/i18n-runtime";
const outcomeMeta = (t: (key: string, fallback: string) => string): Record<ArchiveEntry["outcome"], { tone: "success" | "warn" | "danger" | "neutral" | "info"; label: string }> => ({
  served: { tone: "success", label: t("queues.archive.outcomeServed", "Served") },
  left: { tone: "warn", label: t("queues.archive.outcomeLeft", "Left queue") },
  "no-show": { tone: "danger", label: t("queues.archive.outcomeNoShow", "No-show") },
  removed: { tone: "neutral", label: t("queues.archive.outcomeRemoved", "Removed") },
  transferred: { tone: "info", label: t("queues.archive.outcomeTransferred", "Transferred") },
});
const thStyle: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, fontSize: 12, color: T.soft, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" };
const tdStyle: CSSProperties = { padding: "12px 16px", verticalAlign: "middle", fontSize: 13, color: T.n2, borderBottom: `1px solid ${T.border}` };

export default function QueueArchive() {
  const t = useT();
  const OUTCOME_META = outcomeMeta(t);
  const nav = useNav();
  const isMobile = useIsMobile();
  const counts = useQueueSectionCounts();
  const [locationId, setLocationId] = useState<string | null>(null);
  const { data, loading, error, reload } = useWfResource<{ entries: ArchiveEntry[] }>(`qarchive|${locationId ?? "all"}`, () => listQueueArchive(locationId));
  const [view, setView] = useViewMode("qarchive", "list");
  const [query, setQuery] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState<string | null>(null);
  const [queueFilter, setQueueFilter] = useState<string | null>(null);
  const [sort, setSort] = useState("joined");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [detail, setDetail] = useState<ArchiveEntry | null>(null);

  const entries = data?.entries ?? [];
  const queueNames = useMemo(() => [...new Set(entries.map((e) => e.queueName))], [entries]);

  const onHeaderSort = (k: string) => {
    if (sort === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setSortDir(["joined", "wait", "service"].includes(k) ? "desc" : "asc"); }
  };
  const rows = useMemo(() => {
    const qy = query.trim().toLowerCase();
    let out = entries.filter((e) =>
      (!qy || e.name.toLowerCase().includes(qy) || e.phone.includes(qy) || (e.service ?? "").toLowerCase().includes(qy)) &&
      (!outcomeFilter || OUTCOME_META[e.outcome].label === outcomeFilter) &&
      (!queueFilter || e.queueName === queueFilter));
    const val = (e: ArchiveEntry): string | number => sort === "queue" ? e.queueName : sort === "location" ? e.locationName
      : sort === "wait" ? e.waitMins : sort === "service" ? (e.serviceMins ?? -1) : sort === "outcome" ? e.outcome
      : sort === "name" ? e.name.toLowerCase() : e.joinedAt;
    out = [...out].sort((a, b) => { const x = val(a), y = val(b); const base = x < y ? -1 : x > y ? 1 : 0; return sortDir === "desc" ? -base : base; });
    return out;
  }, [entries, query, outcomeFilter, queueFilter, sort, sortDir]);

  const stats = useMemo(() => {
    const served = entries.filter((e) => e.outcome === "served");
    return {
      total: entries.length,
      served: served.length,
      leftPct: entries.length ? Math.round((entries.filter((e) => e.outcome === "left" || e.outcome === "no-show").length / entries.length) * 100) : 0,
      avgWait: entries.length ? Math.round(entries.reduce((s, e) => s + e.waitMins, 0) / entries.length) : 0,
    };
  }, [entries]);

  const when = (e: ArchiveEntry) => new Date(e.joinedAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const filtersActive = !!(query || outcomeFilter || queueFilter);

  const listView = (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
        <thead>
          <tr>
            {[["name", t("queues.archive.colQueuer", "Queuer")], ["queue", t("queues.archive.colQueue", "Queue")], ["location", t("queues.archive.colLocation", "Location")], ["joined", t("queues.archive.colJoined", "Joined")], ["wait", t("queues.archive.colWaited", "Waited")], ["service", t("queues.archive.colService", "Service")], ["outcome", t("queues.archive.colOutcome", "Outcome")]].map(([k, label]) => (
              <th key={k} style={thStyle} onClick={() => onHeaderSort(k)} aria-sort={sort === k ? (sortDir === "asc" ? "ascending" : "descending") : undefined}>{label}{SORT_ARROW(sort === k, sortDir)}</th>
            ))}
            <th style={{ ...thStyle, cursor: "default", textAlign: "right" }}>{t('queues.archive.text')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} data-testid="qarchive-table-row" data-id={e.id} style={{ cursor: "pointer" }} onClick={() => setDetail(e)}>
              <td style={{ ...tdStyle, fontWeight: 700, color: T.n1 }}>
                {e.name}{e.party > 1 ? <span style={{ fontWeight: 400, color: T.soft }}> +{e.party - 1}</span> : null}
                <div style={{ fontSize: 11.5, fontWeight: 400, color: T.soft, marginTop: 2 }}>{e.service ?? t("queues.archive.walkIn", "Walk-in")} · {e.channel === "sms" ? "SMS" : t('queues.archive.text2')} · {e.source}</div>
              </td>
              <td style={tdStyle}>{e.queueName}</td>
              <td style={tdStyle}>{e.locationName}</td>
              <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>{when(e)}</td>
              <td style={{ ...tdStyle, fontFamily: T.fontMono }}>{e.waitMins}{t('queues.archive.text3')}</td>
              <td style={{ ...tdStyle, fontFamily: T.fontMono }}>{e.serviceMins != null ? <>{e.serviceMins}{t('queues.archive.text3')}</> : "—"}</td>
              <td style={tdStyle}><Pill tone={OUTCOME_META[e.outcome].tone} dot>{OUTCOME_META[e.outcome].label}</Pill></td>
              <td style={tdStyle} onClick={(ev) => ev.stopPropagation()}>
                <RowActions onView={() => setDetail(e)} viewLabel={t("queues.archive.actionViewVisit", "View visit")} moreHeader={e.name} moreItems={[]} testid="qarchive-row" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const cardView = (
    <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 14 }}>
      {rows.map((e) => (
        <div key={e.id} data-testid="qarchive-card" data-id={e.id} onClick={() => setDetail(e)}
          style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: T.shadowSm, padding: 14, display: "flex", flexDirection: "column", gap: 8, cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}{e.party > 1 ? /* TODO(i18n): replace template-literal with t('queues.archive.text5', { ... }) */` +${e.party - 1}` : ""}</span>
            <Pill tone={OUTCOME_META[e.outcome].tone} dot>{OUTCOME_META[e.outcome].label}</Pill>
          </div>
          <div style={{ fontSize: 12, color: T.soft }}>{e.queueName} · {e.locationName}</div>
          <div style={{ fontSize: 12, color: T.soft }}>{when(e)} {t('queues.archive.text6')}{" "}<b style={{ color: T.n1, fontFamily: T.fontMono }}>{e.waitMins}{t('queues.archive.text3')}</b>{e.serviceMins != null ? <> {t('queues.archive.text7')}{" "}<b style={{ color: T.n1, fontFamily: T.fontMono }}>{e.serviceMins}{t('queues.archive.text3')}</b></> : null}</div>
        </div>
      ))}
    </div>
  );

  return (
    <Screen rail="q-board" tabs={queuesSectionTabs(nav, "archive", counts)} activeTab="archive">
      <PageHeader title={t('queues.archive.title')} subtitle={t('queues.archive.subtitle')} breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} testid="qarchive-view" />
          {can("queuemetrics.export") ? <Btn variant="secondary" icon={<Icon.csv size={13}/>} onClick={() => tabExport("queue history", rows as unknown as Record<string, unknown>[])} testid="qarchive-export-btn">{t('queues.archive.text8')}</Btn> : null}
        </>} />
      <DataSourceStrip screen="qarchive" ops={["listQueueArchive"]} />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t('queues.archive.label6')} value={stats.total} icon={<Icon.users size={14}/>} accent={T.p1} />
        <StatTile label={t('queues.archive.label')} value={stats.served} icon={<Icon.check size={14}/>} accent={T.green1} />
        <StatTile label={t('queues.archive.label7')} value={`${stats.leftPct}%`} icon={<Icon.x size={14}/>} accent={stats.leftPct > 15 ? T.red1 : T.n2} />
        <StatTile label={t('queues.archive.label8')} value={`${stats.avgWait}m`} icon={<Icon.clock size={14}/>} accent={T.tDarkBlue} />
      </div>
      <FilterBar>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('queues.archive.placeholder')} aria-label={t('queues.archive.ariaLabel')} data-testid="qarchive-search-input"
          style={{ height: 34, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 13, fontFamily: T.font, width: 240 }} />
        <FilterDropdown label={outcomeFilter || t("queues.archive.filterAnyOutcome", "Any outcome")} active={!!outcomeFilter} options={Object.values(OUTCOME_META).map((o) => o.label)} onPick={setOutcomeFilter} onClear={() => setOutcomeFilter(null)} />
        <FilterDropdown label={queueFilter || t("queues.archive.filterAnyQueue", "Any queue")} active={!!queueFilter} options={queueNames} onPick={setQueueFilter} onClear={() => setQueueFilter(null)} />
        <LocationFilter value={locationId} onChange={setLocationId} />
        {filtersActive ? <button onClick={() => { setQuery(""); setOutcomeFilter(null); setQueueFilter(null); }} style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: "transparent", color: T.p1, border: "none", cursor: "pointer", fontFamily: T.font }}>{t('queues.archive.text9')}</button> : null}
      </FilterBar>
      <div style={{ padding: "0 24px 24px", flex: 1, overflow: "auto" }}>
        {error ? <WfPending screen="qarchive" opName="listQueueArchive" error={error} onRetry={reload} />
        : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t('queues.archive.text10')}</div>
        : rows.length === 0 ? (
          <EmptyState icon={<Icon.clock size={20}/>} title={filtersActive ? t("queues.archive.emptyFilteredTitle", "No visits match these filters") : t("queues.archive.emptyNoneTitle", "No queue history yet")} body={filtersActive ? t("queues.archive.emptyFilteredBody", "Try clearing a filter.") : t("queues.archive.emptyNoneBody", "Completed visits appear here as customers move through your queues.")} />
        ) : view === "list" && !isMobile ? listView : cardView}
      </div>

      {detail ? (
        <div onClick={() => setDetail(null)} role="dialog" aria-label={t("queues.archive.ariaLabel2", "Visit — {name}").replace("{name}", detail.name)} style={{ position: "fixed", inset: 0, zIndex: 250, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: "94vw", background: T.white, borderRadius: 14, boxShadow: T.shadowLg, padding: 22, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: T.n1 }}>{detail.name}{detail.party > 1 ? <span style={{ fontSize: 13, color: T.soft, fontWeight: 500 }}> +{detail.party - 1}</span> : null}</div>
                <div style={{ fontSize: 12, color: T.soft }}>{detail.phone} · {detail.channel === "sms" ? "SMS" : t('queues.archive.text2')}</div>
              </div>
              <Pill tone={OUTCOME_META[detail.outcome].tone} dot>{OUTCOME_META[detail.outcome].label}</Pill>
              <IconBtn label={t('queues.archive.label9')} onClick={() => setDetail(null)}><Icon.x size={15}/></IconBtn>
            </div>
            {([
              [t("queues.archive.detailQueue", "Queue"), detail.queueName], [t("queues.archive.detailLocation", "Location"), detail.locationName], [t("queues.archive.detailService", "Service"), detail.service ?? t("queues.archive.walkIn", "Walk-in")],
              [t("queues.archive.detailJoined", "Joined"), when(detail)], [t("queues.archive.detailSource", "Source"), detail.source], [t("queues.archive.detailWaited", "Waited"), t("queues.archive.minutesValue", "{n} min").replace("{n}", String(detail.waitMins))],
              [t("queues.archive.detailServiceTime", "Service time"), detail.serviceMins != null ? t("queues.archive.minutesValue", "{n} min").replace("{n}", String(detail.serviceMins)) : "—"], [t("queues.archive.detailMessages", "Messages exchanged"), String(detail.messages)],
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, borderBottom: `1px solid ${T.border}`, paddingBottom: 8 }}>
                <span style={{ color: T.soft }}>{k}</span><span style={{ fontWeight: 600, color: T.n1, textAlign: "right" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Screen>
  );
}
